/**
 * Getting Started Documentation
 */
import type { DocPage } from '../types';

export const gettingStartedDocs: DocPage[] = [
  {
    id: 'getting-started/overview',
    title: 'Overview',
    category: 'getting-started',
    tags: ['introduction', 'overview', 'concepts', 'qsl', 'workflow', 'quick start'],
    version: '2.0',
    related: ['getting-started/strategy-lifecycle', 'getting-started/research-process'],
    order: 1,
    description: 'Learn the Quanthop research workflow and get started with strategy development',
    content: `# Learn the Quanthop Research Workflow

Quanthop is a systematic trading research platform built around one principle: **strategies should be validated before they are trusted.**

Strategies are written in [QSL (Quanthop Strategy Language)](/docs/getting-started/strategy-lifecycle) — a clean JavaScript-based format designed for deterministic, reproducible backtesting.

## The Research Workflow

Every strategy follows the same validation pipeline:

\`\`\`
  Idea  ──>  Backtest  ──>  Optimize  ──>  WFA  ──>  Adaptive Flow
   │            │               │            │              │
   │        Single run      Parameter     Rolling       Continuous
   │        against         search        out-of-       performance
   │        historical      for stable    sample        monitoring
   │        data            regions       validation
   │
   Write QSL
   strategy
\`\`\`

Each stage filters out strategies that are not robust enough for the next. Most ideas fail early — and that is by design.

## Quick Start

1. **Create a project** — Open the [Research Lab](/docs/dashboard/research-lab) and choose a strategy template or start from blank
2. **Write a strategy** — Define parameters, indicators, and trading logic in [QSL](/docs/getting-started/strategy-lifecycle)
3. **Run a backtest** — Test against [historical data](/docs/backtesting/running-a-backtest) for a single symbol and interval
4. **Optimize parameters** — Search for [stable parameter regions](/docs/strategy-optimization/overview) instead of peak values
5. **Validate with WFA** — Run [Walk-Forward Analysis](/docs/strategy-optimization/walk-forward-analysis) to test on unseen data
6. **Monitor with Adaptive Flow** — Track live performance and detect [strategy degradation](/docs/strategy-optimization/adaptive-flow)

## What Makes Quanthop Different

The platform is designed around **research integrity**:

- Results that look too good are questioned, not celebrated
- [Out-of-sample testing](/docs/concepts/walk-forward-analysis) is mandatory, not optional
- Rejection is a valid outcome
- [Overfitting](/docs/concepts/overfitting) is treated as the primary risk

## Quick Example

\`\`\`javascript
function define(ctx) {
  ctx.param('period', { type: 'int', default: 14, min: 5, max: 50 });
}

function init(ctx) {
  ctx.indicator('ema', 'EMA', { period: ctx.p.period, source: 'close' });
}

function onBar(ctx, i) {
  const ema = ctx.ind.ema[i];
  const close = ctx.series.close[i];
  
  if (q.isNaN(ema)) return;
  
  if (close > ema && ctx.position('ASSET').qty === 0) {
    ctx.order.market('ASSET', 1, { signal: 'buy' });
  }
}
\`\`\`

## What the Engine Does

The [backtest engine](/docs/engine/execution/order-timing):

1. Parses your strategy and extracts [parameters](/docs/params/defining)
2. Pre-computes all declared [indicators](/docs/api/indicators/sma) (fast, on the host)
3. Calls \`onBar()\` for each candle with indicator values ready
4. Processes [orders](/docs/api/orders/market) according to explicit fill rules
5. Tracks positions, PnL, and [performance metrics](/docs/engine/performance-metrics)

## Next Steps

- [Strategy Lifecycle](/docs/getting-started/strategy-lifecycle) — Understand the three QSL functions
- [The Research Process](/docs/getting-started/research-process) — Complete strategy research guide
- [Your First Strategy](/docs/examples/minimal-strategy) — Minimal working example
- [Quanthop Platform Overview](/docs/dashboard/overview) — Navigating the interface
`,
  },
  {
    id: 'getting-started/strategy-lifecycle',
    title: 'Strategy Lifecycle',
    category: 'getting-started',
    tags: ['lifecycle', 'execution', 'define', 'init', 'onBar', 'qsl'],
    version: '2.0',
    related: ['api/lifecycle/define', 'api/lifecycle/onBar'],
    order: 2,
    description: 'Execution order and lifecycle hooks',
    content: `# Strategy Lifecycle

Understanding when your code runs is essential for writing correct strategies.

## The Three Functions

Every QSL strategy has exactly three functions:

| Function | Purpose | Called |
|----------|---------|--------|
| \`define(ctx)\` | Declare parameters | Once when strategy loads |
| \`init(ctx)\` | Declare indicators, initialize state | Once before backtest |
| \`onBar(ctx, i)\` | Trading logic | Once per candle |

## Execution Order

\`\`\`
1. define(ctx)     — Parameters registered
2. init(ctx)       — Indicators declared, state initialized
3. [Engine computes all indicators]
4. For each bar i:
   a. onBar(ctx, i) — Your logic runs with pre-computed data
   b. Orders queued
5. [Engine processes all orders with fill rules]
\`\`\`

## Lifecycle Functions

### \`define(ctx)\`

Declare your strategy parameters. Called once when the strategy loads.

\`\`\`javascript
function define(ctx) {
  ctx.param('fastLength', {
    type: 'int',
    label: 'Fast EMA',
    default: 9,
    min: 5,
    max: 50,
    optimize: true,
    group: 'Trend'
  });
  
  ctx.param('slowLength', {
    type: 'int',
    label: 'Slow EMA',
    default: 21,
    min: 10,
    max: 200,
    optimize: true,
    group: 'Trend'
  });
}
\`\`\`

### \`init(ctx)\`

Declare indicators and initialize state. Called once before the backtest starts.

\`\`\`javascript
function init(ctx) {
  // Declare indicators (computed by engine, not your code)
  ctx.indicator('fastEma', 'EMA', { period: ctx.p.fastLength, source: 'close' });
  ctx.indicator('slowEma', 'EMA', { period: ctx.p.slowLength, source: 'close' });
  
  // Initialize state
  ctx.state.tradeCount = 0;
  ctx.state.lastSignalBar = -1;
}
\`\`\`

**Key insight**: Indicators are computed by the engine on the host side. By the time \`onBar\` runs, all indicator values for all bars are already calculated.

### \`onBar(ctx, i)\`

Your trading logic. Called for every bar in the dataset.

\`\`\`javascript
function onBar(ctx, i) {
  const fast = ctx.ind.fastEma[i];
  const slow = ctx.ind.slowEma[i];
  
  // Skip if indicators not ready
  if (q.isNaN(fast) || q.isNaN(slow)) return;
  
  // Check for crossover
  if (q.crossOver(ctx.ind.fastEma, ctx.ind.slowEma, i)) {
    ctx.order.market('ASSET', 1, { signal: 'buy' });
    ctx.state.tradeCount++;
  }
  
  if (q.crossUnder(ctx.ind.fastEma, ctx.ind.slowEma, i)) {
    ctx.order.close('ASSET', { signal: 'sell' });
  }
}
\`\`\`

**Important rules:**

- \`onBar\` runs **after** the bar closes
- You see the bar's OHLCV data, but cannot act within it
- Orders placed here execute on the **next** bar's open

## Accessing Data in onBar

\`\`\`javascript
function onBar(ctx, i) {
  // Parameters
  const period = ctx.p.period;
  
  // Indicators (pre-computed arrays)
  const ema = ctx.ind.ema[i];
  const macd = ctx.ind.macd.macd[i];  // Multi-output indicators
  
  // Price series
  const close = ctx.series.close[i];
  const prevClose = ctx.series.close[i - 1];
  const high = ctx.series.high[i];
  
  // Position info
  const pos = ctx.position('ASSET');
  if (pos.qty > 0) { /* in position */ }
  
  // Mutable state
  ctx.state.lastSignalBar = i;
}
\`\`\`

## Stateless vs Stateful

Strategies can be **stateless** (logic based only on current bar and indicators) or **stateful** (maintaining custom state across bars).

### Stateless Example

\`\`\`javascript
function onBar(ctx, i) {
  // Only uses current values — no memory needed
  if (ctx.ind.rsi[i] < 30 && ctx.position('ASSET').qty === 0) {
    ctx.order.market('ASSET', 1, { signal: 'buy' });
  }
}
\`\`\`

### Stateful Example

\`\`\`javascript
function onBar(ctx, i) {
  const rsi = ctx.ind.rsi[i];
  
  // Track consecutive oversold bars
  if (rsi < 30) {
    ctx.state.oversoldCount = (ctx.state.oversoldCount || 0) + 1;
  } else {
    ctx.state.oversoldCount = 0;
  }
  
  // Enter after 3 consecutive oversold bars
  if (ctx.state.oversoldCount >= 3 && ctx.position('ASSET').qty === 0) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'oversold_3bars' });
  }
}
\`\`\`

## Common Mistakes

1. **Assuming orders fill immediately** — They don't. Orders fill on the next bar.
2. **Computing indicators in onBar** — Use \`ctx.indicator()\` in \`init()\` instead.
3. **Forgetting to check \`q.isNaN()\`** — Indicators have warm-up periods.
4. **Using \`Math.random()\`** — Non-deterministic logic breaks reproducibility.

## Related

- [define() Reference](/docs/api/lifecycle/define) — Parameter declaration
- [init() Reference](/docs/api/lifecycle/init) — Indicator declaration
- [onBar() Reference](/docs/api/lifecycle/onBar) — Trading logic
- [Order Execution Model](/docs/engine/execution/order-timing)
`,
  },
  {
    id: 'getting-started/research-process',
    title: 'The Strategy Research Process',
    category: 'getting-started',
    tags: ['research', 'process', 'workflow', 'pipeline', 'validation', 'guide', 'pillar'],
    version: '2.0',
    related: ['getting-started/overview', 'backtesting/running-a-backtest', 'strategy-optimization/overview', 'strategy-optimization/walk-forward-analysis', 'strategy-optimization/adaptive-flow'],
    order: 3,
    description: 'The complete strategy research workflow from idea to deployment',
    content: `# The Strategy Research Process

This is the complete guide to researching a trading strategy on Quanthop. It covers every stage from initial idea to live monitoring.

Most strategies fail. The purpose of research is to discover this **before** risking capital — not after.

## Stage 1: Idea Generation

Every strategy starts with a hypothesis about market behavior:

- "Trend-following with moving average crossovers captures directional moves"
- "RSI extremes predict mean-reverting price action"
- "Volatility breakouts from Bollinger Bands signal continuation"

The hypothesis does not need to be novel. It needs to be **testable**.

Write your idea as a [QSL strategy](/docs/getting-started/strategy-lifecycle) using the three required functions: \`define()\`, \`init()\`, and \`onBar()\`.

## Stage 2: Baseline Testing

Run a single [backtest](/docs/backtesting/running-a-backtest) with default parameters to see if the idea has any merit at all.

**What to look for:**

- Does the strategy produce trades? (If not, your entry logic may be too restrictive)
- Are results clearly better than random? (Compare to a buy-and-hold benchmark)
- Is the [equity curve](/docs/backtesting/reading-results) reasonable? (Smooth growth, not one lucky trade)

**What to ignore at this stage:**

- Exact return numbers (parameters are not optimized yet)
- Small differences between intervals or symbols

If the baseline is clearly unprofitable across multiple configurations, the idea may not be worth pursuing. Move on.

## Stage 3: Parameter Optimization

If the baseline shows promise, use [optimization](/docs/strategy-optimization/running-optimization) to search the parameter space systematically.

**Key principle:** You are not looking for the best parameters. You are looking for **stable regions** where many nearby parameter values produce similar results.

A sharp peak in the optimization surface means the strategy only works with one exact configuration — this is [overfitting](/docs/concepts/overfitting).

A broad plateau means the strategy works across a range of values — this is robustness.

Quanthop provides:

- [Plateau detection](/docs/strategy-optimization/reading-optimization-results) to identify stable zones
- [Stability scoring](/docs/strategy-optimization/reading-optimization-results) to rank parameter clusters
- Baseline scan for multi-asset validation

## Stage 4: Walk-Forward Analysis

[Walk-Forward Analysis (WFA)](/docs/strategy-optimization/walk-forward-analysis) is the most important validation step.

WFA divides your data into rolling windows. For each window:

1. The optimizer finds the best parameters on in-sample data
2. The strategy runs with those parameters on out-of-sample data it has never seen
3. The window rolls forward and repeats

The out-of-sample results are the ones that matter. If a strategy performs well in-sample but fails out-of-sample, it is [overfitted](/docs/concepts/overfitting).

**What good WFA results look like:**

- Positive aggregate out-of-sample returns
- [Consistent parameter selection](/docs/concepts/parameter-stability) across windows
- In-sample and out-of-sample results in the same direction

## Stage 5: Stability Analysis

After WFA, analyze the robustness of your results:

- Do the same parameters work across [different markets](/docs/strategy-optimization/walk-forward-analysis)?
- How sensitive is performance to small parameter changes?
- Are there enough trades for [statistical significance](/docs/backtesting/reading-results)?

If the strategy passes these checks, it has earned a degree of confidence. If not, it joins the majority — safely rejected before any capital was at risk.

## Stage 6: Adaptive Flow Monitoring

A validated strategy does not stay validated forever. Markets change. [Adaptive Flow](/docs/strategy-optimization/adaptive-flow) monitors deployed strategies and detects degradation:

- Tracks live performance against historical expectations
- Flags regime changes that may affect the strategy
- Suggests reoptimization when parameters drift

This creates a continuous feedback loop: validate, deploy, monitor, reoptimize.

## Summary

| Stage | Purpose | Tool |
|-------|---------|------|
| Idea | Define a testable hypothesis | [QSL Editor](/docs/dashboard/research-lab) |
| Baseline | Check if the idea has merit | [Backtest](/docs/backtesting/running-a-backtest) |
| Optimize | Find stable parameter regions | [Optimization](/docs/strategy-optimization/running-optimization) |
| WFA | Validate on unseen data | [Walk-Forward Analysis](/docs/strategy-optimization/walk-forward-analysis) |
| Stability | Confirm robustness | [Results Analysis](/docs/backtesting/reading-results) |
| Monitor | Track live performance | [Adaptive Flow](/docs/strategy-optimization/adaptive-flow) |

## Related Concepts

- [What Is Walk-Forward Analysis?](/docs/concepts/walk-forward-analysis) — The theory behind WFA
- [Strategy Overfitting Explained](/docs/concepts/overfitting) — Why most backtests lie
- [Parameter Stability](/docs/concepts/parameter-stability) — What makes parameters robust
`,
  },
];
